/**
 * 账号密码验证工具
 * Account and Password Validation Utilities
 */

export interface ValidationResult {
  isValid: boolean
  error?: string
}

/**
 * 账号验证规则
 * Account Validation Rules:
 * - 只允许字母、数字、下划线、连字符
 * - 不能有空白字符
 * - 长度 3-20 个字符
 * - 必须以字母或数字开头
 */
export const validateAccount = (account: string): ValidationResult => {
  // 检查是否为空
  if (!account || account.trim() === '') {
    return {
      isValid: false,
      error: 'validation.account.required'
    }
  }

  // 检查长度
  if (account.length < 3) {
    return {
      isValid: false,
      error: 'validation.account.tooShort'
    }
  }

  if (account.length > 20) {
    return {
      isValid: false,
      error: 'validation.account.tooLong'
    }
  }

  // 检查是否包含空白字符
  if (/\s/.test(account)) {
    return {
      isValid: false,
      error: 'validation.account.noWhitespace'
    }
  }

  // 检查是否只包含允许的字符（字母、数字、下划线、连字符）
  if (!/^[a-zA-Z0-9_-]+$/.test(account)) {
    return {
      isValid: false,
      error: 'validation.account.invalidCharacters'
    }
  }

  // 检查是否以字母或数字开头
  if (!/^[a-zA-Z0-9]/.test(account)) {
    return {
      isValid: false,
      error: 'validation.account.mustStartWithAlphanumeric'
    }
  }

  return { isValid: true }
}

/**
 * 密码验证规则
 * Password Validation Rules:
 * - 不能包含空白字符
 * - 最小长度 6 个字符
 * - 最大长度 50 个字符
 * - 必须包含字母和数字（可选，默认开启）
 */
export const validatePassword = (
  password: string,
  options: { requireAlphanumeric?: boolean } = { requireAlphanumeric: true }
): ValidationResult => {
  // 检查是否为空
  if (!password) {
    return {
      isValid: false,
      error: 'validation.password.required'
    }
  }

  // 检查最小长度
  if (password.length < 6) {
    return {
      isValid: false,
      error: 'validation.password.tooShort'
    }
  }

  // 检查最大长度
  if (password.length > 50) {
    return {
      isValid: false,
      error: 'validation.password.tooLong'
    }
  }

  // 检查是否包含空白字符
  if (/\s/.test(password)) {
    return {
      isValid: false,
      error: 'validation.password.noWhitespace'
    }
  }

  // 检查是否包含字母和数字（可选）
  if (options.requireAlphanumeric) {
    const hasLetter = /[a-zA-Z]/.test(password)
    const hasNumber = /[0-9]/.test(password)

    if (!hasLetter || !hasNumber) {
      return {
        isValid: false,
        error: 'validation.password.requireAlphanumeric'
      }
    }
  }

  // 检查是否包含危险字符（例如控制字符）
  // eslint-disable-next-line no-control-regex
  if (/[\x00-\x1F\x7F]/.test(password)) {
    return {
      isValid: false,
      error: 'validation.password.invalidCharacters'
    }
  }

  return { isValid: true }
}

/**
 * 显示名称验证规则
 * Display Name Validation Rules:
 * - 不能全是空白
 * - 长度 1-50 个字符
 * - 允许中文、字母、数字、空格等
 */
export const validateDisplayName = (name: string): ValidationResult => {
  // 检查是否为空
  if (!name || name.trim() === '') {
    return {
      isValid: false,
      error: 'validation.name.required'
    }
  }

  // 检查去除空白后的长度
  const trimmedName = name.trim()
  if (trimmedName.length < 1) {
    return {
      isValid: false,
      error: 'validation.name.required'
    }
  }

  if (trimmedName.length > 50) {
    return {
      isValid: false,
      error: 'validation.name.tooLong'
    }
  }

  // 检查是否包含控制字符或其他危险字符
  // eslint-disable-next-line no-control-regex
  if (/[\x00-\x1F\x7F]/.test(name)) {
    return {
      isValid: false,
      error: 'validation.name.invalidCharacters'
    }
  }

  return { isValid: true }
}

/**
 * 密码确认验证
 * Password Confirmation Validation
 */
export const validatePasswordConfirmation = (
  password: string,
  confirmPassword: string
): ValidationResult => {
  if (!confirmPassword) {
    return {
      isValid: false,
      error: 'validation.confirmPassword.required'
    }
  }

  if (password !== confirmPassword) {
    return {
      isValid: false,
      error: 'validation.confirmPassword.mismatch'
    }
  }

  return { isValid: true }
}

/**
 * 通用验证工具
 * General Validation Utilities
 */

// 检查字符串是否只包含允许的字符
export const containsOnlyAllowedChars = (str: string, pattern: RegExp): boolean => {
  return pattern.test(str)
}

// 检查是否包含空白字符
export const hasWhitespace = (str: string): boolean => {
  return /\s/.test(str)
}

// 检查长度是否在范围内
export const isLengthInRange = (str: string, min: number, max: number): boolean => {
  return str.length >= min && str.length <= max
}

// 清理字符串（去除首尾空白）
export const sanitizeInput = (input: string): string => {
  return input.trim()
}

/**
 * 默认导出所有验证函数
 */
export default {
  validateAccount,
  validatePassword,
  validateDisplayName,
  validatePasswordConfirmation,
  containsOnlyAllowedChars,
  hasWhitespace,
  isLengthInRange,
  sanitizeInput
}
